"use client";
import { useMemo, useState } from "react";

import { BreadcrumbSchema, ItemListSchema } from "@/components/JsonLd";
import PageShell from "@/components/PageShell";
import Engines from "@/components/sections/Engines";

import { type Feature, FEATURES, type FeatureStatus, type Group,GROUPS } from "./_data/features";

const STATUS_TONE: Record<FeatureStatus, { label: string; bg: string; fg: string; border: string }> = {
  live: { label: "Live", bg: "rgba(22,163,74,.08)", fg: "var(--green)", border: "rgba(22,163,74,.25)" },
  beta: { label: "Beta", bg: "rgba(184,110,44,.08)", fg: "var(--cu)", border: "rgba(184,110,44,.3)" },
  soon: { label: "Soon", bg: "var(--b100)", fg: "rgba(19,18,16,.5)", border: "var(--b200)" },
};

export default function FeaturesPage() {
  const [filter, setFilter] = useState<FeatureStatus | "all">("all");
  const [q, setQ] = useState("");

  const counts = useMemo(() => {
    const acc = { all: 0, live: 0, beta: 0, soon: 0 } as Record<FeatureStatus | "all", number>;
    for (const f of FEATURES) {
      acc.all += 1;
      acc[f.status] += 1;
    }
    return acc;
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return FEATURES.filter((f) => {
      if (filter !== "all" && f.status !== filter) return false;
      if (!needle) return true;
      return `${f.title} ${f.oneLine}`.toLowerCase().includes(needle);
    });
  }, [filter, q]);

  // Group filtered features for the catalogue (non-spotlight groups only)
  const cataloguedByGroup = useMemo(() => {
    const groupKeys = (Object.keys(GROUPS) as Group[]).filter((g) => !GROUPS[g].spotlight);
    return groupKeys.map((g) => ({
      key: g,
      items: filtered.filter((f) => f.group === g),
    })).filter((g) => g.items.length > 0);
  }, [filtered]);


  return (
    <>
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Features", url: "/features" },
        ]}
      />
      {/* "soon" features are noindexed placeholders and stay out of the list,
          exactly as they stay out of the sitemap. */}
      <ItemListSchema
        items={FEATURES.filter((f) => f.status !== "soon").map((f) => ({
          name: f.title,
          url: `/features/${f.slug}`,
        }))}
        name="LexVio capabilities"
        path="/features"
      />
      <PageShell>
        <style>{`
          .feat-hub-hero{display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1fr);gap:60px;align-items:center;padding:72px clamp(20px,4vw,52px) 52px;border-bottom:1px solid var(--b200)}
          .feat-hub-intro{display:grid;grid-template-columns:minmax(0,1.5fr) minmax(0,1fr);gap:48px;padding:32px clamp(20px,4vw,52px) 24px;align-items:center}
          @media (max-width: 960px){
            .feat-hub-hero,.feat-hub-intro{grid-template-columns:1fr;gap:32px}
          }
        `}</style>
        <section className="feat-hub-hero">
          <div>
            <div className="ey rev">What we have</div>
            <h1 className="sh2 rev d1" style={{ fontSize: "clamp(36px,4.5vw,58px)" }}>
              Every feature, named.{" "}<br />
              <em>Every status, honest.</em>
            </h1>
            <p className="rev d2" style={{ fontSize: "16px", color: "rgba(19,18,16,.55)", maxWidth: "640px", marginTop: "16px", lineHeight: 1.8 }}>
              A complete inventory of what LexVio ships today — and what&apos;s in beta or shortly behind it.
              If you don&apos;t see <strong>Live</strong>, we&apos;re still building it. Click any feature for the full story.
            </p>
          </div>

          {/* Inventory at a glance — fills the right of the hero */}
          <div className="rev d3" style={{
            background: "var(--o950)", color: "var(--b50)",
            borderRadius: "6px", padding: "28px 30px",
            border: "1px solid rgba(251,250,246,.08)",
          }}>
            <div style={{
              fontSize: "10px", letterSpacing: ".18em", textTransform: "uppercase",
              color: "rgba(251,250,246,.5)", marginBottom: "20px",
            }}>
              Inventory at a glance
            </div>
            <div style={{
              fontFamily: "var(--font-fraunces), serif", fontSize: "56px", fontWeight: 200,
              letterSpacing: "-.03em", lineHeight: 1, color: "var(--b50)",
            }}>
              {counts.all}
              <span style={{ fontSize: "16px", color: "rgba(251,250,246,.5)", marginLeft: "10px", letterSpacing: 0 }}>
                features
              </span>
            </div>
            <div style={{ marginTop: "24px", display: "flex", flexDirection: "column", gap: "10px" }}>
              {[
                { k: "live" as const, label: "Live", desc: "Shipping today" },
                { k: "beta" as const, label: "Beta", desc: "Real users, real workloads" },
                { k: "soon" as const, label: "Soon", desc: "On the roadmap" },
              ].map(({ k, label, desc }) => {
                const tone = STATUS_TONE[k];
                return (
                  <button
                    key={k}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
                      padding: "12px 14px",
                      background: "rgba(251,250,246,.04)",
                      border: `1px solid ${filter === k ? "var(--cu)" : "rgba(251,250,246,.1)"}`,
                      borderRadius: "4px", cursor: "pointer", textAlign: "left",
                      transition: "border-color .2s",
                    }}
                    type="button"
                    onClick={() => setFilter(k)}
                  >
                    <div>
                      <div style={{
                        fontSize: "10px", letterSpacing: ".14em", textTransform: "uppercase",
                        padding: "2px 7px", border: `1px solid ${tone.border}`,
                        background: tone.bg, color: tone.fg,
                        borderRadius: "2px", display: "inline-block", marginBottom: "6px",
                      }}>
                        {label}
                      </div>
                      <div style={{ fontSize: "12px", color: "rgba(251,250,246,.6)" }}>
                        {desc}
                      </div>
                    </div>
                    <div style={{
                      fontFamily: "var(--font-fraunces), serif", fontSize: "26px", fontWeight: 200,
                      color: "var(--b50)", letterSpacing: "-.02em",
                    }}>
                      {counts[k]}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Vio + Nexus — the two intelligence engines (same rich
            mockup-cards as the landing page) */}
        <div id="vio" style={{ scrollMarginTop: "80px", position: "relative" }}>
          <span aria-hidden="true" id="nexus" style={{ position: "absolute", top: 0, visibility: "hidden", scrollMarginTop: "80px" }} />
          <Engines />
        </div>


        {/* Filter strip */}
        <section style={{ padding: "32px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)", background: "var(--b100)" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px" }}>
            <input
              // No "GST" in the example set: the tax features it pointed at are
              // withheld here, and the term is itself an India-only one on a
              // global product (§1A).
              placeholder="Search every feature — e.g. redlining, SCIM, citations, vault, Vio, Nexus…"
              style={{
                flex: 1, minWidth: "280px", padding: "12px 16px",
                border: "1px solid var(--b200)", borderRadius: "4px",
                background: "var(--b50)", fontSize: "14px", color: "var(--o900)", outline: "none",
              }}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div style={{ display: "flex", border: "1px solid var(--b200)", borderRadius: "4px", overflow: "hidden" }}>
              {(["all", "live", "beta", "soon"] as const).map((s) => {
                const active = filter === s;
                return (
                  <button
                    key={s}
                    style={{
                      padding: "10px 16px",
                      background: active ? "var(--o900)" : "var(--b50)",
                      color: active ? "var(--b50)" : "var(--o900)",
                      border: "none", cursor: "pointer", fontSize: "12px",
                      textTransform: "capitalize",
                      display: "flex", alignItems: "center", gap: "8px",
                    }}
                    type="button"
                    onClick={() => setFilter(s)}
                  >
                    {s}
                    <span style={{
                      fontSize: "10px", opacity: active ? .7 : .5,
                      padding: "1px 6px",
                      background: active ? "rgba(255,255,255,.15)" : "var(--b100)",
                      borderRadius: "10px",
                    }}>
                      {counts[s]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <p style={{ fontSize: "11px", color: "rgba(19,18,16,.5)", marginTop: "10px" }}>
            Showing {filtered.length} of {FEATURES.length} features.
          </p>
        </section>

        {/* Catalogue — grouped by capability area, not by product module */}
        {cataloguedByGroup.length === 0 ? (
          <section style={{ padding: "80px clamp(20px,4vw,52px)", textAlign: "center", borderBottom: "1px solid var(--b200)" }}>
            <p style={{ fontSize: "14px", color: "rgba(19,18,16,.5)" }}>
              No features match &ldquo;{q}&rdquo; with the {filter} filter.
            </p>
          </section>
        ) : (
          cataloguedByGroup.map((g, gi) => {
            const meta = GROUPS[g.key];
            return (
              <section key={g.key} id={g.key} style={{
                padding: "48px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)",
                background: gi % 2 ? "var(--b100)" : "transparent",
                scrollMarginTop: "80px",
              }}>
                <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: "44px", alignItems: "start" }}>
                  <div style={{ position: "sticky", top: "84px" }}>
                    <div style={{ fontSize: "10px", letterSpacing: ".14em", textTransform: "uppercase", color: "rgba(19,18,16,.5)", marginBottom: "8px" }}>
                      {g.items.length} {g.items.length === 1 ? "feature" : "features"}
                    </div>
                    <div style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "26px", fontWeight: 200, color: "var(--o900)", letterSpacing: "-.01em", lineHeight: 1.15, marginBottom: "10px" }}>
                      {meta.label}
                    </div>
                    <p style={{ fontSize: "13px", color: "rgba(19,18,16,.6)", lineHeight: 1.7 }}>{meta.sub}</p>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    {g.items.map((f) => <FeatureCard key={f.slug} feature={f} />)}
                  </div>
                </div>
              </section>
            );
          })
        )}

        {/* CTA */}
        <section style={{ padding: "72px clamp(20px,4vw,52px)", textAlign: "center", background: "var(--o950)" }}>
          <h2 style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "clamp(28px,3.5vw,42px)", fontWeight: 200, color: "var(--b50)", marginBottom: "14px" }}>
            See it on your own contracts.{" "}<br />
            <em style={{ color: "var(--cl)", fontStyle: "italic" }}>Get started in 60 seconds.</em>
          </h2>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <a className="cta-p" data-mag="true" href={`/sign-up`}><span className="cta-p-t">Sign up free →</span></a>
            <a className="cta-s" href="/scan">Try the scanner</a>
          </div>
        </section>
      </PageShell>
    </>
  );
}

function FeatureCard({ feature }: { feature: Feature }) {
  const tone = STATUS_TONE[feature.status];
  return (
    <a
      href={`/features/${feature.slug}`}
      style={{
        padding: "18px 20px",
        background: "var(--b50)",
        border: "1px solid var(--b200)",
        borderRadius: "5px",
        textDecoration: "none",
        display: "block",
        opacity: feature.status === "soon" ? 0.85 : 1,
        transition: "border-color .2s, transform .2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--cu)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--b200)";
        e.currentTarget.style.transform = "";
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", marginBottom: "6px" }}>
        <div style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "15.5px", fontWeight: 400, color: "var(--o900)", lineHeight: 1.3 }}>
          {feature.title}
        </div>
        {/* There is no jurisdiction badge here any more, and there is nothing
            left to badge: `FEATURES` excludes every market-tagged entry, so a
            capability scoped to one jurisdiction's law never reaches this
            card. The badge was the old containment and it was not enough — a
            footnote on a feature that 404s at any price is still an
            advertisement for it. See features/_data/features.ts. */}
        <span style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          <span style={{
            fontSize: "9px", letterSpacing: ".14em", textTransform: "uppercase",
            padding: "3px 8px", border: `1px solid ${tone.border}`,
            background: tone.bg, color: tone.fg,
            borderRadius: "2px", whiteSpace: "nowrap",
          }}>
            {tone.label}
          </span>
        </span>
      </div>
      <p style={{ fontSize: "12.5px", color: "rgba(19,18,16,.62)", lineHeight: 1.65 }}>
        {feature.oneLine}
      </p>
    </a>
  );
}
